// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

/**
 * UnassignedPatientBanner — lightweight, NON-blocking reminder (Phase 4).
 *
 * Shows on the dashboard ONLY when a master clinic already has ACTIVE
 * sub-accounts AND still has unassigned patients AND hasn't turned the reminder
 * off AND isn't inside a 7-day snooze. Per Henry's PRD §9 it must never block,
 * never pop a modal, and a clinic with no team to delegate to is never nagged.
 * "Turn off reminders" persists settings.patientAssignmentReminderEnabled=false
 * on the master account; "Remind me later" snoozes for SNOOZE_DAYS.
 *
 * Self-contained: does its own counts via direct fetch so it doesn't entangle
 * the dashboard's patient pipeline.
 */
import { Alert, Box, Button } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { useGetIdentity, useNotify, useTranslate } from 'react-admin'
import { deadlineFetch } from '../hooks/customEndpointFetch'
import { ASSIGN_REMINDER_SNOOZE_KEY } from '../storeConfig'
import { authHeaders } from '../utils/api'

// "Remind me later" USED to be a sessionStorage flag, which is gone the moment
// the vet opens a new tab — so in practice the banner was permanent and the
// only real escape was the nuclear "Turn off reminders". It is now a DATED
// snooze in localStorage: quiet for a week, then it comes back.
const SNOOZE_KEY = ASSIGN_REMINDER_SNOOZE_KEY
export const SNOOZE_DAYS = 7
const SNOOZE_MS = SNOOZE_DAYS * 24 * 60 * 60 * 1000

// Storage can throw (Safari private mode, disabled cookies). A reminder is not
// worth breaking the dashboard over — degrade to "not snoozed".
const readSnoozeUntil = (): number => {
  try {
    const raw = localStorage.getItem(SNOOZE_KEY)
    if (!raw) return 0
    const until = Number(raw)
    // Number('') === 0 and Number(null) === 0 — both are caught by !raw above;
    // anything else non-finite is a corrupt value, so clear it.
    if (!Number.isFinite(until) || until <= 0) {
      localStorage.removeItem(SNOOZE_KEY)
      return 0
    }
    return until
  } catch {
    return 0
  }
}

const isSnoozed = (): boolean => {
  const until = readSnoozeUntil()
  if (!until) return false
  if (Date.now() >= until) {
    try {
      localStorage.removeItem(SNOOZE_KEY)
    } catch {
      /* best effort */
    }
    return false
  }
  return true
}

const countFrom = async (url: string): Promise<number> => {
  const res = await deadlineFetch(url, { headers: authHeaders() })
  if (!res.ok) return 0
  const data = await res.json().catch(() => ({}))
  return typeof data.totalDocs === 'number' ? data.totalDocs : 0
}

// Sub-accounts you can actually delegate to. `masterAccount` alone counts
// pending_invitation / disabled / removed rows too, which is how a clinic whose
// only invitee never accepted (and therefore has NO team) got nagged anyway —
// exactly the case the PRD says must never be nagged.
const activeSubsQuery = (masterId: number | string) =>
  `/api/users?where%5Band%5D%5B0%5D%5BmasterAccount%5D%5Bequals%5D=${masterId}` +
  `&where%5Band%5D%5B1%5D%5BsubAccountStatus%5D%5Bequals%5D=active&limit=1`

const UnassignedPatientBanner: React.FC = () => {
  const translate = useTranslate()
  const notify = useNotify()
  const { identity } = useGetIdentity()
  const [subCount, setSubCount] = useState<number | null>(null)
  const [unassigned, setUnassigned] = useState<number>(0)
  const [reminderOn, setReminderOn] = useState(true)
  const [dismissed, setDismissed] = useState(() => isSnoozed())

  const isMaster =
    (identity?.role === 'vet' || identity?.role === 'vetadmin') && !identity?.masterAccount

  useEffect(() => {
    if (!isMaster || dismissed) return
    let alive = true
    ;(async () => {
      // current reminder setting (master account)
      try {
        const meRes = await deadlineFetch('/api/users/me', { headers: authHeaders() })
        const me = await meRes.json().catch(() => ({}))
        const enabled = (me?.user || me)?.settings?.patientAssignmentReminderEnabled
        if (alive && enabled === false) {
          setReminderOn(false)
          return
        }
      } catch {
        /* default on */
      }
      const [subs, unassignedPets] = await Promise.all([
        countFrom(activeSubsQuery(identity!.id)),
        countFrom('/api/pets?where%5BassignedVet%5D%5Bexists%5D=false&limit=1'),
      ])
      if (!alive) return
      setSubCount(subs)
      setUnassigned(unassignedPets)
    })()
    return () => {
      alive = false
    }
  }, [isMaster, dismissed, identity])

  const turnOff = async () => {
    try {
      // This PATCH REPLACES `settings` wholesale, so the GET is not optional
      // context — it is the rest of the object. An unchecked read that still
      // returns JSON (401/5xx with an error body) collapses to `{}` and the
      // write then DELETES the sibling keys, `glucoseUnit` among them: the
      // vet's unit choice survives locally (RaStorevetpo.glucoseUnit wins) and
      // reappears wrong on their next device, seeded geographically instead.
      // Same read-modify-write shape as useGlucoseUnitPreference.changeGlucoseUnit,
      // which checks both responses — this one now does too.
      const meRes = await deadlineFetch('/api/users/me', { headers: authHeaders() })
      if (!meRes.ok) throw new Error(`GET /api/users/me failed: ${meRes.status}`)
      const me = (await meRes.json().catch(() => ({})))?.user
      if (!me) throw new Error('GET /api/users/me returned no user')
      const patchRes = await deadlineFetch(`/api/users/${identity!.id}`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({
          settings: { ...(me.settings || {}), patientAssignmentReminderEnabled: false },
        }),
      })
      // Reporting success on a rejected write leaves the reminder ON server-side
      // while the vet is told it is off — the banner returns on the next load
      // and reads as a bug in the button.
      if (!patchRes.ok) throw new Error(`PATCH /api/users failed: ${patchRes.status}`)
      setReminderOn(false)
      notify('pos.clinic_team.reminder_turned_off', { type: 'info' })
    } catch {
      notify('pos.clinic_team.error_network', { type: 'error' })
    }
  }

  const dismiss = () => {
    try {
      localStorage.setItem(SNOOZE_KEY, String(Date.now() + SNOOZE_MS))
    } catch {
      /* storage unavailable — the snooze then lasts only this mount */
    }
    setDismissed(true)
  }

  // Only nag when: master · reminder on · not snoozed · HAS ACTIVE subs · HAS unassigned.
  if (!isMaster || !reminderOn || dismissed || !subCount || unassigned === 0) return null

  return (
    <Box sx={{ mb: 2 }}>
      <Alert
        severity="info"
        action={
          <>
            <Button
              color="inherit"
              size="small"
              onClick={() => {
                // hash-router SPA — navigate without a Router hook dependency
                window.location.hash = '#/pets'
              }}
            >
              {translate('pos.clinic_team.reminder_assign')}
            </Button>
            <Button color="inherit" size="small" onClick={dismiss}>
              {translate('pos.clinic_team.reminder_later')}
            </Button>
            <Button color="inherit" size="small" onClick={turnOff}>
              {translate('pos.clinic_team.reminder_turn_off')}
            </Button>
          </>
        }
      >
        {translate('pos.clinic_team.reminder_text', { count: unassigned })}
      </Alert>
    </Box>
  )
}

export default UnassignedPatientBanner
